/**
 * Compose a one-off email — §15's answer to "just write to this one person".
 *
 * The modal collects three things: who (an email address), optionally who they
 * are (names, only when nobody knows them yet), and what the email should say
 * (a brief). Submitting creates an ordinary email TASK with no sequence —
 * drafted by the same writer, held by the same guards, edited/regenerated/
 * approved in the same pane as every cadence step. This surface deliberately
 * ends where the queue begins.
 *
 * Detection runs as the address is typed (debounced): the server answers from
 * the identity spine first and asks HubSpot live when the spine has no CRM
 * link. The three outcomes are said in words — matched to HubSpot, known to
 * ActOS only, or new — because "will this create a CRM record?" is exactly
 * what a rep wants to know before pressing the button. A recipient the system
 * knows also decides which merge tokens will resolve, so the field list shown
 * here is the recipient's own, not a generic promise.
 *
 * Before the address is complete, the same field autocompletes from people the
 * workspace already knows — half of "write to this one person" is remembering
 * their address. And once detection lands, one line says what the writer will
 * know of the prior exchange, so a brief like "follow up on her reply" is
 * asked with open eyes.
 */

import { useEffect, useRef, useState } from 'react';
import { api, ApiError, relative } from './api';
import { Dialog } from './Dialog';
import { tip, tipAt } from './tips';

/** The fact-level history of the exchange — counts and dial positions, no prose. */
interface Correspondence {
  sent: number;
  lastSentAt: string | null;
  replies: number;
  lastReplyAt: string | null;
  scope: 'last_sent' | 'thread';
  contentIncluded: boolean;
}

interface LookupResult {
  email: string;
  known: boolean;
  inCrm: boolean;
  crmChecked: boolean;
  freeMail: boolean;
  contact: {
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    company: string | null;
  } | null;
  activeSequence: string | null;
  blocked: string | null;
  /**
   * Things worth knowing that do NOT block — a paused cadence, a detected
   * meeting. A pause holds automation; a rep deliberately writing one email
   * is the human decision the pause was waiting for.
   */
  warnings: string[];
  sender: { email: string; pinned: boolean } | null;
  tokens: { name: string; label: string; value: string | null }[];
  /**
   * Optional at runtime, not just in the type: a UI deployed ahead of the API
   * answers lookups without it, and must say nothing rather than guess.
   */
  correspondence?: Correspondence;
}

interface CreateResult {
  id: number;
  detected: 'crm' | 'known' | 'new';
  needsReview: boolean;
  reviewReason: string | null;
  note: string;
}

/** One row from `/tasks/oneoff/suggest` — a person the rep may mean. */
interface Suggestion {
  email: string;
  name: string | null;
  title: string | null;
  company: string | null;
  inCrm: boolean;
  sentCount: number;
  lastSentAt: string | null;
  replyCount: number;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * The correspondence line — three variants, because two dials govern what the
 * writer will see of the prior exchange: `ai.oneoff_context_scope` picks how
 * much of it, `ai.data_policy` whether received content may ride along. A rep
 * about to write "follow up on her reply" deserves to know which world they
 * are in before the draft comes back refusing to know what was said.
 */
function correspondenceLine(c: Correspondence): string | null {
  if (c.sent + c.replies === 0) return null;
  if (c.scope === 'last_sent') {
    return c.sent > 0 ? 'The writer sees your last email to them.' : null;
  }
  if (c.contentIncluded) {
    // The writer's window is 10 messages; claiming more would overstate it.
    const n = Math.min(c.sent + c.replies, 10);
    return n === 1
      ? 'Their last email with you will ground the draft.'
      : `Their last ${n} emails with you will ground the draft.`;
  }
  const wrote = c.sent === 1 ? 'You’ve written once' : `You’ve written ${c.sent} times`;
  if (c.replies === 0) return `${wrote}; they have not replied yet.`;
  const replied = c.replies === 1 ? 'they replied once' : `they replied ${c.replies} times`;
  return `${wrote} and ${replied} — reply contents are withheld from the writer by the AI data policy.`;
}

/** "3 sent · last 12d ago · 1 reply" — the tail that says "you know them". */
function historyTail(s: Suggestion): string {
  return [
    s.sentCount > 0 ? `${s.sentCount} sent` : null,
    s.sentCount > 0 && s.lastSentAt ? `last ${relative(s.lastSentAt)}` : null,
    s.replyCount > 0 ? `${s.replyCount} ${s.replyCount === 1 ? 'reply' : 'replies'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Escape, heard before the Dialog hears it.
 *
 * The Dialog's Escape handler is a `document` capture-phase listener, so an
 * onKeyDown on the To input never sees the key — the Dialog stops the event
 * and closes the whole modal first. Priority on the same node in the same
 * phase is registration order, and effects run child-first, so the way to be
 * earlier in that queue is to register from a component INSIDE the Dialog
 * (tips.ts wins the same race from the other end, by registering before React
 * mounts). That is this component's whole job: mounted once, it dismisses the
 * suggestion list on Escape and stops the event; while no list is showing it
 * leaves the key alone, and Escape closes the modal as it always did.
 */
function EscapeBeforeDialog({ when, onEscape }: { when: boolean; onEscape: () => void }) {
  // Read through refs so the mount-once listener always sees current truth.
  const whenRef = useRef(when);
  whenRef.current = when;
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !whenRef.current) return;
      e.preventDefault();
      // Same node, same phase: nothing weaker keeps the Dialog's handler out
      // of it — Dialog.tsx explains the choice.
      e.stopImmediatePropagation();
      escapeRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  return null;
}

export function ComposeOneOff({
  onClose,
  onCreated,
  initialTo = '',
  initialBrief = '',
}: {
  onClose: () => void;
  /** Called with the new task's id after the draft lands in the queue. */
  onCreated: (taskId: number) => Promise<void>;
  /**
   * Seeds for a follow-up started from a reply (R13). Read once, as state
   * initialisers — the box is a working surface from the moment it opens, and
   * a prop that kept overwriting what the rep typed would be worse than no
   * seed at all. The existing debounced lookup fires on the seeded address by
   * itself, so nothing else needs to know where it came from.
   */
  initialTo?: string;
  initialBrief?: string;
}) {
  const [to, setTo] = useState(initialTo);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [brief, setBrief] = useState(initialBrief);
  const [lookup, setLookup] = useState<LookupResult | null>(null);
  const [looking, setLooking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  /**
   * Debounced detection. 600ms of quiet after a plausible address, then one
   * lookup — and a stale response must never land on top of a newer address,
   * so each request checks it still describes the current input before
   * touching state.
   */
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setLookup(null);
    setError(null);
    if (!EMAIL_SHAPE.test(to.trim())) {
      setLooking(false);
      return;
    }

    setLooking(true);
    const asked = to.trim();
    timerRef.current = window.setTimeout(() => {
      void api<LookupResult>(`/tasks/oneoff/lookup?email=${encodeURIComponent(asked)}`)
        .then((result) => {
          setLookup((prev) => (asked === to.trim() ? result : prev));
        })
        .catch((e: unknown) => {
          if (asked !== to.trim()) return;
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (asked === to.trim()) setLooking(false);
        });
    }, 600);

    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
    // `to` is the one real dependency; the cleanup cancels the stale timer.
  }, [to]);

  /**
   * Debounced autocomplete — guarded by the exact complement of detection's
   * guard, so the list lives only while the address is a fragment and retires
   * the moment it completes (a picked suggestion IS a complete address, which
   * hands off to detection above). Same staleness idiom. Unlike detection,
   * previous rows stay up while a newer query is in flight: a list that
   * blanks between keystrokes shimmers.
   */
  const suggestTimerRef = useRef<number | null>(null);
  /**
   * An Escape dismissal must stick: with a response in flight (or the timer
   * still pending), clearing the rows alone would let it re-open the list the
   * rep just closed. Remember what was dismissed; any other input lifts it.
   */
  const dismissedRef = useRef<string | null>(null);
  useEffect(() => {
    if (suggestTimerRef.current !== null) window.clearTimeout(suggestTimerRef.current);
    const asked = to.trim();
    if (dismissedRef.current !== asked) dismissedRef.current = null;
    if (asked.length < 2 || EMAIL_SHAPE.test(asked)) {
      setSuggestions((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    suggestTimerRef.current = window.setTimeout(() => {
      void api<{ suggestions: Suggestion[] }>(
        `/tasks/oneoff/suggest?q=${encodeURIComponent(asked)}`,
      )
        .then((result) => {
          setSuggestions((prev) =>
            asked === to.trim() && dismissedRef.current !== asked ? result.suggestions : prev,
          );
        })
        .catch(() => {
          // A typeahead that fails is a typeahead that shows nothing —
          // never a compose error.
          if (asked === to.trim()) setSuggestions((prev) => (prev.length === 0 ? prev : []));
        });
    }, 250);

    return () => {
      if (suggestTimerRef.current !== null) window.clearTimeout(suggestTimerRef.current);
    };
  }, [to]);

  /**
   * The background sweep. The moment detection reports a KNOWN contact, ask
   * the API to refresh this person's correspondence — its own invocation, its
   * own subrequest budget — so the CREATE that follows (which deliberately
   * does not sweep) reads context Neon already holds. Two passes on the API
   * side: re-read the threads ActOS sent on, and search the rep's OWN inbox
   * for exchange with this address that ActOS never conducted (§15). That
   * second pass is why this no longer waits for prior ActOS sends — the
   * record most worth fetching is exactly the one ActOS does not hold yet.
   * (No scope guard either: the sent half it captures feeds the writer under
   * every scope, including 'last_sent'.)
   *
   * Fire-and-forget by design: no failure here is the rep's problem, so no
   * failure here may become a compose error. Once per address; re-lookups of
   * the same one do not re-fire. When the sweep DID land something new, the
   * lookup re-runs so the history line describes what the writer will now
   * actually see.
   */
  const sweptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lookup?.known || lookup.blocked || lookup.freeMail) return;
    if (sweptRef.current === lookup.email) return;
    sweptRef.current = lookup.email;
    const asked = lookup.email;
    void api<{ swept: boolean; ingested: number; sentCaptured?: number }>('/tasks/oneoff/sweep', {
      method: 'POST',
      body: JSON.stringify({ email: asked }),
    })
      .then((result) => {
        // Only a sweep that changed the record earns a second lookup — and it
        // lands only while the rep still composes to the same address.
        if (!result.swept || result.ingested + (result.sentCaptured ?? 0) === 0) return;
        return api<LookupResult>(`/tasks/oneoff/lookup?email=${encodeURIComponent(asked)}`).then(
          (fresh) => {
            setLookup((prev) => (prev && prev.email === asked ? fresh : prev));
          },
        );
      })
      .catch(() => {
        // Swallowed on purpose — see above.
      });
  }, [lookup]);

  const validAddress = EMAIL_SHAPE.test(to.trim());
  const blocked = lookup?.blocked ?? null;
  const freeMail = lookup?.freeMail ?? false;
  const canSubmit = validAddress && !busy && !blocked && !freeMail;

  const detectedName = lookup?.contact
    ? [lookup.contact.firstName, lookup.contact.lastName].filter(Boolean).join(' ') || null
    : null;

  const available = (lookup?.tokens ?? []).filter((t) => t.value);
  const missing = (lookup?.tokens ?? []).filter((t) => !t.value);

  // The complement of detection's guard: 2+ characters that are not yet an
  // address. Empty results simply show nothing — no box, no "no matches".
  const showSuggestions = !validAddress && to.trim().length >= 2 && suggestions.length > 0;

  /** Escape's half of the bargain: hide the list and keep it hidden. */
  const dismissSuggestions = () => {
    dismissedRef.current = to.trim();
    if (suggestTimerRef.current !== null) window.clearTimeout(suggestTimerRef.current);
    setSuggestions([]);
  };

  const historyLine = lookup?.correspondence ? correspondenceLine(lookup.correspondence) : null;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<CreateResult>('/tasks/oneoff', {
        method: 'POST',
        body: JSON.stringify({
          to: to.trim(),
          // Names only matter for a person nobody knows yet; for a detected
          // contact the record's own values win and these are not sent.
          ...(lookup && !lookup.known && !lookup.inCrm
            ? {
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
              }
            : {}),
          brief: brief.trim() || undefined,
        }),
      });

      /**
       * A person ActOS did not know exists as a contact only NOW — the
       * pre-create sweep found "nobody by that address" and stored nothing.
       * That covers detected 'new' AND 'crm' (a HubSpot person live-linked
       * at CREATE — the contact most likely to carry the cadence history
       * this sweep exists to surface), which is why the guard is "was not
       * known before", not "is brand new". Sweep again so the Gmail record
       * is in place for the very next thing the rep plausibly does: hit
       * Regenerate on the draft, which re-reads the record. The FIRST draft
       * ships without that context; stated trade-off, not an accident —
       * CREATE's own subrequest budget cannot absorb a Gmail sweep.
       * Fire-and-forget for the same reason the pre-create sweep is.
       */
      if (!lookup?.known) {
        void api('/tasks/oneoff/sweep', {
          method: 'POST',
          body: JSON.stringify({ email: to.trim() }),
        }).catch(() => {
          // Swallowed on purpose — a failed sweep is never a compose error.
        });
      }

      await onCreated(result.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} label="New one-off email" variant="modal" busy={busy}>
      <header className="modal-head">
        <div>
          <p className="kicker">One-off email</p>
          <h2>Write to one person</h2>
        </div>
        <button
          type="button"
          className="link-btn"
          disabled={busy}
          {...tipAt('Closes the compose box. Nothing has been created yet.', 'end')}
          onClick={onClose}
        >
          Close
        </button>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="field" {...tipAt('The recipient. ActOS checks HubSpot as you type.', 'start')}>
          <span>To</span>
          <input
            type="email"
            value={to}
            autoFocus
            placeholder="person@company.com"
            onChange={(e) => setTo(e.target.value)}
            // Enter while the list is up is aimed at a suggestion, not the
            // form — implicit submission would create the task from a stray
            // keystroke (Enroll.tsx documents this bug class on its search
            // box). Escape never reaches here; see EscapeBeforeDialog.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && showSuggestions) e.preventDefault();
            }}
          />
        </label>

        {/* ---- Autocomplete, in the flow --------------------------------- */}
        <EscapeBeforeDialog when={showSuggestions} onEscape={dismissSuggestions} />
        {showSuggestions && (
          <ul className="token-list">
            {suggestions.map((s) => {
              const tail = historyTail(s);
              return (
                <li key={s.email}>
                  <button
                    type="button"
                    className="token-row"
                    // onMouseDown, so picking does not first blur the To input
                    // out from under the caret — the TokenPicker trick.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setTo(s.email);
                    }}
                    // Keyboard activation arrives as a click with no
                    // mousedown — a Tab-focused row must still pick.
                    onClick={() => setTo(s.email)}
                  >
                    <span className="token-label">{s.name || s.email}</span>
                    <span className="token-value">
                      {s.company ? `${s.email} · ${s.company}` : s.email}
                    </span>
                    {tail && <span className="token-value">{tail}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* ---- Detection, said in words ---------------------------------- */}
        {looking && <p className="fine">Checking who this is&hellip;</p>}

        {lookup && !looking && (
          <>
            {freeMail ? (
              <p className="error-note">
                One-off emails need a work address — ActOS routes and caps outreach by company,
                and a personal address has no company to belong to.
              </p>
            ) : blocked ? (
              <p className="error-note">{blocked}</p>
            ) : lookup.inCrm ? (
              <p className="advisory-note">
                <strong>{detectedName ?? lookup.email}</strong>
                {lookup.contact?.title ? ` — ${lookup.contact.title}` : ''}
                {lookup.contact?.company ? ` at ${lookup.contact.company}` : ''}. Already in
                HubSpot; the email lands on their existing record.
              </p>
            ) : lookup.known ? (
              <p className="advisory-note">
                <strong>{detectedName ?? lookup.email}</strong> is known to ActOS
                {lookup.contact?.company ? ` (${lookup.contact.company})` : ''} but not linked to
                HubSpot yet — approving the send creates the HubSpot record.
              </p>
            ) : (
              <p className="advisory-note">
                New person. Approving the send creates them in HubSpot
                {lookup.crmChecked ? '' : ' (HubSpot could not be checked just now)'} — add their
                name below so the email and the record get it right.
              </p>
            )}

            {lookup.activeSequence && !blocked && (
              <p className="advisory-note">
                Heads up: they are mid-cadence in &ldquo;{lookup.activeSequence}&rdquo;. This
                one-off sends alongside it — a reply still stops that cadence.
              </p>
            )}

            {!blocked &&
              (lookup.warnings ?? []).map((warning) => (
                <p key={warning} className="advisory-note">
                  {warning}
                </p>
              ))}

            {/* Name fields only for a person nobody has on record. */}
            {!lookup.known && !lookup.inCrm && !freeMail && !blocked && (
              <div className="field-row">
                <label className="field">
                  <span>First name (optional)</span>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </label>
                <label className="field">
                  <span>Last name (optional)</span>
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </label>
              </div>
            )}

            {/* The recipient's OWN token list — what will actually resolve. */}
            {!freeMail && !blocked && (
              <p className="fine">
                {available.length > 0 && (
                  <>
                    Merge fields that resolve for them:{' '}
                    {available.map((t) => t.label.toLowerCase()).join(', ')}.
                  </>
                )}
                {missing.length > 0 && (
                  <>
                    {' '}
                    Empty (need a fallback if used): {missing.map((t) => t.label.toLowerCase()).join(', ')}.
                  </>
                )}
              </p>
            )}

            {lookup.sender && !blocked && (
              <p className="fine">
                Sends from {lookup.sender.email}
                {lookup.sender.pinned ? ' — pinned to this contact (§5.5.3)' : ''}.
              </p>
            )}

            {/* What the writer will know of the exchange so far — worth one
                line before the brief asks for a follow-up. */}
            {historyLine && !blocked && !freeMail && <p className="fine">{historyLine}</p>}
          </>
        )}

        <label
          className="field"
          {...tipAt(
            'Defines the draft: with a brief, the email says what it asks and nothing more — no sales pitch or template around it. Left empty, the writer writes a standard outreach note from what it knows about this person. Either way it keeps to known facts — it cannot invent detail to satisfy a brief.',
            'start',
          )}
        >
          <span>What should it say? (optional)</span>
          <textarea
            rows={3}
            value={brief}
            maxLength={1000}
            placeholder="e.g. follow up on the webinar — offer a 15-minute walkthrough of the reporting module"
            onChange={(e) => setBrief(e.target.value)}
          />
        </label>

        {error && <p className="error-note">{error}</p>}

        <div className="draft-actions">
          <button
            type="submit"
            className="primary"
            disabled={!canSubmit}
            {...tip(
              'Drafts the email now and puts it in your queue to review. Nothing sends until you approve it there — video, tokens and regeneration all work from the queue.',
            )}
          >
            {busy ? 'Writing…' : 'Write the draft'}
          </button>
        </div>

        <p className="fine">
          The draft opens in your queue: edit it, insert tokens, attach a video, regenerate with a
          steer — approving assigns a send slot in the recipient&rsquo;s business hours, like any
          other email.
        </p>
      </form>
    </Dialog>
  );
}
